import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import type {
  ContactRequest,
  ContactTopic,
  UserSummary,
} from "@papertiger/contracts";
import { api } from "./api.js";
import { IntegrationsGuide } from "./IntegrationsGuide.js";

/** Public routes rendered outside the authenticated trading dashboard. */
export type PublicRoute =
  | "home"
  | "pricing"
  | "about"
  | "integrations"
  | "support"
  | "privacy"
  | "terms"
  | "disclaimer"
  | "security"
  | "not-found";

/** Metadata attached to a public route. */
interface PageMetadata {
  /** Browser and social title. */
  title: string;
  /** Search and social description. */
  description: string;
  /** Canonical route path. */
  path: string;
  /** Whether search engines should index the page. */
  index: boolean;
}

/** Props for the public site router. */
interface PublicSiteProps {
  /** Public page to render. */
  route: PublicRoute;
  /** Account card shown only on the logged-out home page. */
  accountCard?: ReactNode;
}

/** Account-aware destination shown in shared public navigation. */
export interface PublicAccountNavigation {
  /** Link destination. */
  href: string;
  /** Visible link label. */
  label: string;
}

/** Metadata for every indexable public page. */
const PAGE_METADATA: Record<PublicRoute, PageMetadata> = {
  home: {
    title: "PaperTiger — Paper trade prediction markets",
    description:
      "PaperTiger simulates prediction-market orders using public Polymarket and Kalshi order books. No real orders or funds.",
    path: "/",
    index: true,
  },
  pricing: {
    title: "PaperTiger pricing",
    description:
      "Compare PaperTiger Free, Trial, and Paid account access and pricing.",
    path: "/pricing",
    index: true,
  },
  about: {
    title: "About PaperTiger",
    description:
      "Technical overview of the PaperTiger prediction-market paper-trading application.",
    path: "/about",
    index: true,
  },
  integrations: {
    title: "PaperTiger for ChatGPT, Codex, and Claude",
    description:
      "Connect PaperTiger to ChatGPT, Codex, or Claude, authenticate securely, and use every hosted paper-trading research capability.",
    path: "/integrations",
    index: true,
  },
  support: {
    title: "PaperTiger Support",
    description:
      "Get help with PaperTiger, report an account issue, or share beta feedback.",
    path: "/support",
    index: true,
  },
  privacy: {
    title: "PaperTiger Privacy Policy",
    description:
      "How PaperTiger collects, uses, stores, and shares personal information.",
    path: "/privacy",
    index: true,
  },
  terms: {
    title: "PaperTiger Terms of Use",
    description: "The terms that apply when using PaperTiger and its public API.",
    path: "/terms",
    index: true,
  },
  disclaimer: {
    title: "PaperTiger Risk Disclaimer",
    description:
      "Important limitations of simulated prediction-market trading and market data.",
    path: "/disclaimer",
    index: true,
  },
  security: {
    title: "PaperTiger Security",
    description:
      "Security practices and responsible vulnerability reporting for PaperTiger.",
    path: "/security",
    index: true,
  },
  "not-found": {
    title: "Page not found — PaperTiger",
    description: "The requested PaperTiger page could not be found.",
    path: "/404",
    index: false,
  },
};

/** Labels for contact-form topics. */
const CONTACT_TOPIC_LABELS: Record<ContactTopic, string> = {
  support: "Product support",
  feedback: "Beta feedback",
  account: "Account help",
  privacy: "Privacy request",
  security: "Security report",
};

/**
 * Resolves a browser path to a public page.
 *
 * @param pathname Browser pathname.
 * @returns Matching public route or null for app-only paths.
 */
export function resolvePublicRoute(pathname: string): PublicRoute | null {
  const normalized = pathname.replace(/\/+$/u, "") || "/";
  const routes: Record<string, PublicRoute> = {
    "/": "home",
    "/pricing": "pricing",
    "/about": "about",
    "/integrations": "integrations",
    "/support": "support",
    "/privacy": "privacy",
    "/terms": "terms",
    "/disclaimer": "disclaimer",
    "/security": "security",
  };
  if (
    [
      "/docs",
      "/verify",
      "/dashboard",
      "/positions",
      "/orders",
      "/backtests",
      "/api-keys",
      "/settings",
      "/admin",
    ].includes(normalized)
  ) {
    return null;
  }
  return routes[normalized] ?? "not-found";
}

/**
 * Returns the account link used by public pages.
 *
 * @param authenticated Whether the browser has a persisted application session.
 * @returns Destination and label for the account navigation item.
 */
export function getPublicAccountNavigation(
  authenticated: boolean,
): PublicAccountNavigation {
  return authenticated
    ? { href: "/dashboard", label: "Dashboard" }
    : { href: "/#join", label: "Create account" };
}

/**
 * Public PaperTiger product and policy site.
 *
 * @param props Route and optional authentication card.
 * @returns Public page.
 */
export function PublicSite(props: PublicSiteProps): React.JSX.Element {
  const metadata = PAGE_METADATA[props.route];
  usePageMetadata(metadata);

  if (props.route === "home") {
    return <LandingPage accountCard={props.accountCard} />;
  }

  return (
    <div className="public-site">
      <PublicHeader />
      {props.route === "about" && <AboutPage />}
      {props.route === "pricing" && <PricingPage />}
      {props.route === "integrations" && <IntegrationsGuide />}
      {props.route === "support" && <SupportPage />}
      {props.route === "privacy" && <PrivacyPage />}
      {props.route === "terms" && <TermsPage />}
      {props.route === "disclaimer" && <DisclaimerPage />}
      {props.route === "security" && <SecurityPage />}
      {props.route === "not-found" && <NotFoundPage />}
      <PublicFooter />
    </div>
  );
}

/**
 * Applies search and social metadata for a client-side route.
 *
 * @param metadata Route metadata.
 * @returns Nothing.
 */
function usePageMetadata(metadata: PageMetadata): void {
  useEffect(() => {
    document.title = metadata.title;
    setMeta("description", metadata.description);
    setMeta("robots", metadata.index ? "index, follow" : "noindex, nofollow");
    setPropertyMeta("og:title", metadata.title);
    setPropertyMeta("og:description", metadata.description);
    setPropertyMeta("og:url", `https://papertigerapp.com${metadata.path}`);
    setPropertyMeta("twitter:title", metadata.title);
    setPropertyMeta("twitter:description", metadata.description);
    const canonical =
      document.querySelector<HTMLLinkElement>('link[rel="canonical"]') ??
      document.head.appendChild(document.createElement("link"));
    canonical.rel = "canonical";
    canonical.href = `https://papertigerapp.com${metadata.path}`;
  }, [metadata]);
}

/**
 * Loads Google Tag Manager only when a valid build-time container is configured.
 *
 * @returns Nothing.
 */
export function useGoogleTagManager(): void {
  useEffect(() => {
    const containerId = import.meta.env.VITE_GTM_CONTAINER_ID as
      | string
      | undefined;
    if (
      containerId === undefined ||
      containerId.length === 0 ||
      !/^GTM-[A-Z0-9]+$/u.test(containerId) ||
      document.getElementById("papertiger-gtm") !== null
    ) {
      return;
    }
    const browserWindow = window as typeof window & {
      dataLayer?: Array<Record<string, string | number>>;
    };
    browserWindow.dataLayer = browserWindow.dataLayer ?? [];
    browserWindow.dataLayer.push({
      "gtm.start": Date.now(),
      event: "gtm.js",
    });
    const script = document.createElement("script");
    script.id = "papertiger-gtm";
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`;
    document.head.appendChild(script);
  }, []);
}

/**
 * Creates or updates a named meta tag.
 *
 * @param name Meta name.
 * @param content Meta content.
 * @returns Nothing.
 */
function setMeta(name: string, content: string): void {
  const element =
    document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`) ??
    document.head.appendChild(document.createElement("meta"));
  element.name = name;
  element.content = content;
}

/**
 * Creates or updates a property meta tag.
 *
 * @param property Open Graph or Twitter property.
 * @param content Meta content.
 * @returns Nothing.
 */
function setPropertyMeta(property: string, content: string): void {
  const element =
    document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`) ??
    document.head.appendChild(document.createElement("meta"));
  element.setAttribute("property", property);
  element.content = content;
}

/**
 * Shared public header.
 *
 * @returns Public navigation.
 */
export function PublicHeader(): React.JSX.Element {
  const accountNavigation = getPublicAccountNavigation(api.hasToken());

  return (
    <header className="public-header">
      <a className="public-brand" href="/" aria-label="PaperTiger home">
        <img src="/brand/papertiger-mark.png" alt="" width="46" height="46" />
        <span>PaperTiger</span>
      </a>
      <nav aria-label="Main navigation">
        <a href="/pricing">Pricing</a>
        <a href="/about">About</a>
        <a href="/integrations">AI integrations</a>
        <a href="/docs">API docs</a>
        <a href="/support">Support</a>
        <a className="nav-cta" href={accountNavigation.href}>
          {accountNavigation.label}
        </a>
      </nav>
    </header>
  );
}

/**
 * Logged-out product landing page.
 *
 * @param props Optional account card.
 * @returns Product landing page.
 */
function LandingPage(props: { accountCard?: ReactNode }): React.JSX.Element {
  return (
    <div className="public-site">
      <PublicHeader />
      <main>
        <section className="landing-hero">
          <div className="landing-copy">
            <p className="eyebrow">PaperTiger</p>
            <h1>Prediction market paper trading</h1>
            <p className="landing-lede">
              Simulate market and limit orders using public Polymarket and
              Kalshi order books. Track virtual wallets, orders, fills,
              positions, profit and loss, and market settlement.
            </p>
            <p className="landing-safety">
              PaperTiger uses virtual funds and never submits an order to a
              prediction-market venue.
            </p>
            <div className="hero-actions">
              <a className="primary link-button" href="#join">Create account</a>
              <a className="secondary link-button" href="/pricing">Compare plans</a>
              <a className="secondary link-button" href="/integrations">Use with AI</a>
              <a className="secondary link-button" href="/docs">API documentation</a>
            </div>
          </div>
          <div className="landing-auth" id="join">
            {props.accountCard}
          </div>
        </section>

        <section className="public-section capability-section">
          <h2>What it does</h2>
          <div className="capability-grid">
            <article>
              <h3>Market data</h3>
              <p>
                Search Polymarket and Kalshi or load a supported public market URL.
              </p>
            </article>
            <article>
              <h3>Order simulation</h3>
              <p>
                Simulate market and limit orders against public order-book depth.
              </p>
            </article>
            <article>
              <h3>Portfolio accounting</h3>
              <p>
                Maintain virtual cash, reservations, positions, fees, activity,
                equity history, and settled results.
              </p>
            </article>
            <article>
              <h3>API access</h3>
              <p>
                Use account-level API keys to manage paper accounts. Trading
                keys can find markets, place paper orders, and read portfolio
                state.
              </p>
            </article>
          </div>
        </section>

        <section className="public-section technical-section">
          <h2>Technical behavior</h2>
          <dl className="technical-list">
            <div><dt>Venues</dt><dd>Polymarket and Kalshi</dd></div>
            <div><dt>Market data</dt><dd>Public venue APIs and order books</dd></div>
            <div><dt>Order types</dt><dd>Market and limit</dd></div>
            <div><dt>Time in force</dt><dd>FOK, IOC, and GTC</dd></div>
            <div><dt>Fill model</dt><dd>Depth, slippage, and venue fees</dd></div>
            <div><dt>Settlement</dt><dd>Public venue lifecycle and resolution data</dd></div>
            <div><dt>Accounts</dt><dd>Free: one wallet; Trial and Paid: up to 25</dd></div>
            <div><dt>Real execution</dt><dd>None</dd></div>
          </dl>
        </section>

        <section className="public-section disclaimer-preview">
          <strong>Simulation only</strong>
          <p>
            PaperTiger is not a broker, exchange, gambling operator, or
            financial adviser. Simulated results can differ from real execution.
          </p>
          <a href="/disclaimer">Risk disclaimer</a>
        </section>
      </main>
      <PublicFooter />
    </div>
  );
}

/**
 * Public comparison of customer account access.
 *
 * @returns Pricing page.
 */
function PricingPage(): React.JSX.Element {
  const [user, setUser] = useState<UserSummary | null | undefined>(undefined);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<"cancel-trial" | "checkout" | "portal" | null>(null);
  useEffect(() => {
    let active = true;
    void api.me()
      .then((currentUser) => {
        if (active) setUser(currentUser);
      })
      .catch(() => {
        if (active) setUser(null);
      });
    return () => {
      active = false;
    };
  }, []);
  const beginCheckout = async (): Promise<void> => {
    setPendingAction("checkout");
    setActionError(null);
    try {
      window.location.assign(await api.startCheckout());
    } catch (error) {
      setActionError(readPublicError(error));
      setPendingAction(null);
    }
  };
  const cancelTrial = async (): Promise<void> => {
    if (!window.confirm("End your free trial now and move to the Free plan? This cannot be undone.")) return;
    setPendingAction("cancel-trial");
    setActionError(null);
    try {
      setUser(await api.cancelTrial());
    } catch (error) {
      setActionError(readPublicError(error));
    } finally {
      setPendingAction(null);
    }
  };
  const openBillingPortal = async (): Promise<void> => {
    setPendingAction("portal");
    setActionError(null);
    try {
      window.location.assign(await api.billingPortal());
    } catch (error) {
      setActionError(readPublicError(error));
      setPendingAction(null);
    }
  };
  const actionForPlan = (plan: UserSummary["accessTier"]): React.JSX.Element => {
    if (user === undefined) {
      return <span className="pricing-action muted">Loading your plan…</span>;
    }
    if (user === null) {
      return (
        <a className={plan === "trial" ? "primary link-button pricing-action" : "secondary link-button pricing-action"} href="/#join">
          {plan === "trial" ? "Start your trial" : plan === "paid" ? "Create account to upgrade" : "Create account"}
        </a>
      );
    }
    if (user.isAdmin) {
      return <button className="secondary pricing-action" type="button" disabled>Administrator access</button>;
    }
    if (user.accessTier === plan) {
      return <button className="secondary pricing-action" type="button" disabled>Current plan</button>;
    }
    if (plan === "trial") {
      return <button className="secondary pricing-action" type="button" disabled>Trial used</button>;
    }
    if (plan === "paid") {
      return (
        <button className="primary pricing-action" type="button" disabled={pendingAction !== null} onClick={() => void beginCheckout()}>
          {pendingAction === "checkout" ? "Opening checkout…" : "Upgrade to Paid"}
        </button>
      );
    }
    if (user.accessTier === "trial") {
      return (
        <button className="secondary pricing-action" type="button" disabled={pendingAction !== null} onClick={() => void cancelTrial()}>
          {pendingAction === "cancel-trial" ? "Downgrading…" : "Cancel trial and downgrade"}
        </button>
      );
    }
    return (
      <button className="secondary pricing-action" type="button" disabled={pendingAction !== null} onClick={() => void openBillingPortal()}>
        {pendingAction === "portal" ? "Opening Billing Portal…" : "Downgrade to Free"}
      </button>
    );
  };
  return (
    <main className="pricing-shell">
      <div className="pricing-heading">
        <p className="eyebrow">Pricing</p>
        <h1>Start free. Upgrade when you need more room.</h1>
        <p>
          Every account uses virtual funds and simulated prediction-market
          execution. Paid access is a monthly subscription and can be managed
          or cancelled through Stripe.
        </p>
      </div>
      <section className="pricing-grid" aria-label="PaperTiger plans">
        <article className="pricing-card">
          <p className="eyebrow">Free</p>
          <h2>$0</h2>
          <p className="pricing-summary">For exploring PaperTiger at your own pace.</p>
          <ul>
            <li>One virtual wallet</li>
            <li>Paper trading, portfolio tracking, and API access</li>
            <li>Backtests unavailable while market-data coverage is paused</li>
          </ul>
          {actionForPlan("free")}
        </article>
        <article className="pricing-card pricing-card-featured">
          <p className="eyebrow">Free trial</p>
          <h2>7 days</h2>
          <p className="pricing-summary">Included after you confirm a new account.</p>
          <ul>
            <li>Up to 25 virtual wallets</li>
            <li>Everything in Free</li>
            <li>Backtest access resumes when the product service returns</li>
          </ul>
          {actionForPlan("trial")}
        </article>
        <article className="pricing-card">
          <p className="eyebrow">Paid</p>
          <h2>$5 <span>/ month</span></h2>
          <p className="pricing-summary">For ongoing, multi-wallet paper-trading work.</p>
          <ul>
            <li>Up to 25 virtual wallets</li>
            <li>Everything in Trial</li>
            <li>Manage or cancel anytime in the Billing Portal</li>
          </ul>
          {actionForPlan("paid")}
        </article>
      </section>
      {actionError !== null && <p className="form-error pricing-error" role="alert">{actionError}</p>}
      <p className="pricing-note">
        Prices are in USD. No real-money trading, deposits, or custody are
        available through PaperTiger.
      </p>
    </main>
  );
}

/**
 * About page.
 *
 * @returns About content.
 */
function AboutPage(): React.JSX.Element {
  return (
    <PolicyLayout eyebrow="About" title="About PaperTiger">
      <p className="policy-lede">
        PaperTiger is a prediction-market paper-trading application and API
        built by Disco Media.
      </p>
      <h2>Purpose</h2>
      <p>
        It provides virtual wallets and simulated execution for testing
        prediction-market workflows without real funds or orders.
      </p>
      <h2>Data and execution</h2>
      <p>
        PaperTiger reads public Polymarket and Kalshi market data. Simulated
        fills walk observed order-book depth rather than using a midpoint or
        proxy price. Market orders are fill-or-kill; limit orders support
        fill-or-kill, immediate-or-cancel, and good-til-canceled instructions.
        The simulator reserves virtual cash or contracts, applies venue fee
        curves and conservative depth participation, records fills and account
        activity, then uses public lifecycle and resolution evidence for
        settlement.
      </p>
      <h2>What is simulated</h2>
      <p>
        Each wallet contains virtual USD only. PaperTiger tracks cash,
        reservations, positions, partial fills, realized and unrealized profit
        and loss, fees, and settlement credits. It may refresh a public book
        before evaluating an open order, but it never sends that order, wallet
        data, or credentials to a trading venue.
      </p>
      <h2>Limits</h2>
      <p>
        PaperTiger does not accept deposits, custody funds, connect a brokerage
        account, or submit an order to Polymarket, Kalshi, or any other venue.
        It is independent software and is not endorsed by or affiliated with
        those venues.
      </p>
      <p>
        A paper fill is not evidence that a real order would fill. Real trading
        can differ because of network latency, queue position, hidden or
        changing liquidity, cancellations, venue eligibility, outages, changing
        fees, and resolution disputes. Public data can be delayed, incomplete,
        or unavailable. Historical Backtests require separate high-fidelity
        market-data coverage and are currently paused while that service is
        rebuilt.
      </p>
      <h2>Beta status</h2>
      <p>
        Market coverage, features, limits, and stored beta data may change.
        Public venue data can be delayed or unavailable.
      </p>
    </PolicyLayout>
  );
}

/**
 * Support and beta feedback page.
 *
 * @returns Support content and contact form.
 */
function SupportPage(): React.JSX.Element {
  return (
    <PolicyLayout eyebrow="Support" title="Support">
      <p className="policy-lede">
        Send product questions, account issues, privacy requests, or beta
        feedback. Messages go to the PaperTiger team at
        {" "}<a href="mailto:admin@papertigerapp.com">admin@papertigerapp.com</a>.
      </p>
      <div className="support-grid">
        <div>
          <h2>Before you send</h2>
          <ul>
            <li>Include the market URL and wallet name for trading issues.</li>
            <li>Do not send passwords, API keys, or exchange credentials.</li>
            <li>For security vulnerabilities, choose “Security report.”</li>
          </ul>
          <h2>Response times</h2>
          <p>
            During beta, we aim to reply within two business days. Urgent venue
            outages may be visible as warnings inside the app and do not mean a
            real trade was placed.
          </p>
        </div>
        <ContactForm />
      </div>
    </PolicyLayout>
  );
}

/**
 * Privacy policy page.
 *
 * @returns Privacy policy.
 */
function PrivacyPage(): React.JSX.Element {
  return (
    <PolicyLayout eyebrow="Legal" title="Privacy Policy">
      <PolicyEffectiveDate />
      <p className="policy-lede">
        This policy explains how Disco Media handles information when you use
        PaperTiger, its API, its ChatGPT and Codex plugin, its Claude connector,
        or its support form.
      </p>
      <h2>Information we collect</h2>
      <p>
        We collect your account email, a one-way password hash, virtual wallet
        and paper-trading activity, experiments, strategy versions, run and
        decision records, API-key and OAuth authorization metadata, Stripe
        customer and subscription identifiers and webhook records, support
        messages, and ordinary technical records such as IP address, request
        time, user agent, and error logs. Raw passwords, raw API keys, and raw
        OAuth tokens are not stored.
      </p>
      <h2>How we use information</h2>
      <p>
        We use it to provide and secure PaperTiger, confirm accounts, maintain
        virtual portfolios, run and explain requested paper-trading experiments,
        link authorized ChatGPT, Codex, and Claude clients, answer support requests,
        prevent abuse, diagnose errors, understand product usage, and meet legal
        obligations.
      </p>
      <h2>Storage and service providers</h2>
      <p>
        PaperTiger uses service providers for hosting, database storage, email
        delivery, billing, and traffic protection, including Railway, Neon,
        Cloudflare, Disco Mail, and Stripe. When you connect PaperTiger to an AI client, the
        PaperTiger account, wallet, market, experiment, run, decision, order,
        position, and performance data needed for your request is returned to
        OpenAI or Anthropic through the authenticated tool connection for use
        in ChatGPT, Codex, or Claude. Public market information is obtained
        from Polymarket and Kalshi. Providers process information under their
        own terms and may operate in other countries.
      </p>
      <h2>Analytics and local storage</h2>
      <p>
        The app uses a secure HTTP-only session cookie so you remain signed in;
        JavaScript cannot read that cookie. A Google Tag Manager integration is available but is not
        loaded unless a container is configured. If enabled, tags may collect
        usage data according to their configuration; this policy will be updated
        if that materially changes our practices.
      </p>
      <h2>Retention and choices</h2>
      <p>
        We keep account and paper-trading records while your account is active
        and as reasonably needed for security, support, dispute resolution, and
        legal compliance. OAuth access tokens expire after one hour, refresh
        tokens rotate, and disconnecting PaperTiger from an AI client
        revokes the connection. You may request access, correction, or deletion
        by using the support form. Some records may be retained where required
        or where deletion would impair security and audit obligations.
      </p>
      <h2>Children and changes</h2>
      <p>
        PaperTiger is for people aged 13 and older. If you are under the age of
        majority where you live, you may use PaperTiger only with permission
        from a parent or legal guardian. We do not knowingly collect personal
        information from anyone under 13. A parent or guardian may contact
        Support to request deletion of an ineligible account. We may update
        this policy as the beta evolves and will revise the effective date when
        we do.
      </p>
      <h2>Contact</h2>
      <p>
        Send privacy questions through <a href="/support">Support</a> or email
        {" "}<a href="mailto:admin@papertigerapp.com">admin@papertigerapp.com</a>.
      </p>
    </PolicyLayout>
  );
}

/**
 * Terms of use page.
 *
 * @returns Terms of use.
 */
function TermsPage(): React.JSX.Element {
  return (
    <PolicyLayout eyebrow="Legal" title="Terms of Use">
      <PolicyEffectiveDate />
      <p className="policy-lede">
        By using PaperTiger, you agree to these terms. If you do not agree, do
        not use the service.
      </p>
      <h2>Eligibility and accounts</h2>
      <p>
        You must be at least 13. If you are under the age of majority where you
        live, your parent or legal guardian must permit you to use PaperTiger.
        By creating an account, you confirm that you meet these requirements.
        Give accurate account information, protect your credentials, and tell
        us promptly if you suspect unauthorized access. You are responsible
        for activity under your account and API keys.
      </p>
      <h2>Paper trading only</h2>
      <p>
        Every wallet balance, order, fill, position, profit, and loss in
        PaperTiger is simulated. No item is cash, a security, a wagering
        instrument, or a claim against Disco Media. It cannot be withdrawn,
        transferred, redeemed, or converted into value.
      </p>
      <h2>Acceptable use</h2>
      <p>
        Do not misuse the service, bypass access controls or rate limits,
        interfere with other users, scrape in a way that harms availability,
        upload malicious content, probe systems without authorization, or use
        PaperTiger unlawfully. Do not use PaperTiger to facilitate real-money
        gambling, investment execution, money or crypto transfers, or evasion
        of a third party's age, identity, location, or access restrictions.
        Respect third-party venue terms and intellectual property.
      </p>
      <h2>Beta availability and changes</h2>
      <p>
        Features, limits, market coverage, simulated balances, and stored data
        may change during beta. We may suspend or end access to protect the
        service, comply with law, or address misuse. We do not promise
        uninterrupted availability or permanent preservation of beta data.
      </p>
      <h2>Paid subscriptions</h2>
      <p>
        A confirmed account begins with a seven-day trial. Free accounts may
        create one virtual wallet; Trial and Paid accounts may create up to 25.
        Paid subscriptions are processed by Stripe and renew monthly until
        cancelled. You may cancel through the Stripe Billing Portal; access
        continues until the end of the paid billing period. Except where law
        requires otherwise, payments are non-refundable. Taxes are not
        calculated by PaperTiger at this time.
      </p>
      <h2>Third-party data and services</h2>
      <p>
        Market data and links come from independent third parties. We do not
        control or endorse those services and cannot guarantee their accuracy,
        legality, timeliness, or availability.
      </p>
      <h2>No warranties; limited liability</h2>
      <p>
        To the extent permitted by law, PaperTiger is provided “as is” and “as
        available,” without warranties. Disco Media is not liable for indirect,
        incidental, special, consequential, or lost-profit damages arising from
        use of the beta. Rights that cannot legally be excluded remain in force.
      </p>
      <h2>Contact and changes</h2>
      <p>
        Questions may be sent through <a href="/support">Support</a>. We may
        update these terms and will revise the effective date; continued use
        after an update means you accept the revised terms.
      </p>
    </PolicyLayout>
  );
}

/**
 * Risk disclaimer page.
 *
 * @returns Risk disclaimer.
 */
function DisclaimerPage(): React.JSX.Element {
  return (
    <PolicyLayout eyebrow="Important" title="Risk Disclaimer">
      <PolicyEffectiveDate />
      <div className="policy-callout prominent">
        <strong>PaperTiger is a simulation.</strong>
        <p>
          It does not execute real trades and is not financial, investment,
          legal, tax, or gambling advice.
        </p>
      </div>
      <h2>Simulated results differ from real results</h2>
      <p>
        Paper fills use observed public order books and software assumptions.
        A real order may face different latency, queue position, liquidity,
        price movement, fees, eligibility rules, rejection behavior, or
        settlement outcomes. Historical and simulated performance do not
        guarantee future performance.
      </p>
      <h2>Market data can be wrong or unavailable</h2>
      <p>
        Venue data may be delayed, incomplete, stale, revised, or temporarily
        inaccessible. Displayed probabilities, marks, P&amp;L, close times, and
        settlement status should not be used to make a real-money decision.
      </p>
      <h2>Prediction markets carry real-world risk</h2>
      <p>
        Real prediction-market participation can involve loss of capital,
        regulatory restrictions, tax consequences, counterparty and platform
        risk, and rapidly changing prices. Laws vary by location. If you choose
        to use a real venue, make your own assessment and seek qualified advice.
      </p>
      <h2>No affiliation</h2>
      <p>
        PaperTiger and Disco Media are not affiliated with, sponsored by, or
        endorsed by Polymarket or Kalshi. Their names and marks belong to their
        respective owners.
      </p>
    </PolicyLayout>
  );
}

/**
 * Security information page.
 *
 * @returns Security content and reporting guidance.
 */
function SecurityPage(): React.JSX.Element {
  return (
    <PolicyLayout eyebrow="Security" title="Security">
      <p className="policy-lede">
        PaperTiger handles virtual portfolios, not deposits or real exchange
        orders. We still treat account and API access as sensitive.
      </p>
      <h2>What we protect</h2>
      <p>
        Passwords are one-way hashed, API secrets are displayed once, and
        browser and API traffic use HTTPS in production. Security headers
        restrict framing, content sources, and unnecessary browser permissions.
        The database-free liveness endpoint avoids needless data access.
      </p>
      <h2>Your part</h2>
      <p>
        Use a unique password, keep API keys out of browser code and source
        control, revoke keys you no longer need, and never send credentials in a
        support message.
      </p>
      <h2>Report a vulnerability</h2>
      <p>
        Please report suspected vulnerabilities privately. Include a concise
        description, reproduction steps, and impact. Do not access other users’
        data, disrupt the service, or publicly disclose an issue before we have
        had a reasonable chance to respond.
      </p>
      <ContactForm defaultTopic="security" />
    </PolicyLayout>
  );
}

/**
 * Contact form shared by support and security pages.
 *
 * @param props Optional initial topic.
 * @returns Contact form.
 */
function ContactForm(props: {
  defaultTopic?: ContactTopic;
}): React.JSX.Element {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [topic, setTopic] = useState<ContactTopic>(
    props.defaultTopic ?? "support",
  );
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  /**
   * Sends the validated form to the public support endpoint.
   *
   * @param event Browser form event.
   * @returns Nothing.
   */
  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setNotice(null);
    const contact: ContactRequest = {
      name,
      email,
      topic,
      message,
      website,
    };
    try {
      const response = await api.contact(contact);
      setNotice(response.message);
      setMessage("");
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Your message could not be sent.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="contact-form surface" onSubmit={(event) => void submit(event)}>
      <h2>Send a message</h2>
      <div className="form-two-column">
        <label>
          Name
          <input
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoComplete="name"
            minLength={2}
            maxLength={120}
            required
          />
        </label>
        <label>
          Email
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            required
          />
        </label>
      </div>
      <label>
        Topic
        <select
          value={topic}
          onChange={(event) => setTopic(event.target.value as ContactTopic)}
        >
          {Object.entries(CONTACT_TOPIC_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </label>
      <label>
        Message
        <textarea
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          minLength={20}
          maxLength={5_000}
          rows={7}
          placeholder="Tell us what happened, what you expected, and any safe steps to reproduce it."
          required
        />
      </label>
      <label className="honeypot" aria-hidden="true">
        Website
        <input
          type="text"
          value={website}
          onChange={(event) => setWebsite(event.target.value)}
          tabIndex={-1}
          autoComplete="off"
        />
      </label>
      <button className="primary" disabled={busy}>
        {busy ? "Sending…" : "Send message"}
      </button>
      <p className="form-privacy">
        Do not include passwords or API keys. By sending this form, you agree
        that we may use its contents to respond as described in our
        {" "}<a href="/privacy">Privacy Policy</a>.
      </p>
      {notice !== null && <div className="notice" role="status">{notice}</div>}
    </form>
  );
}

/**
 * Shared policy and information-page layout.
 *
 * @param props Eyebrow, heading, and page content.
 * @returns Policy layout.
 */
function PolicyLayout(props: {
  eyebrow: string;
  title: string;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <main className="policy-shell">
      <header className="policy-heading">
        <p className="eyebrow">{props.eyebrow}</p>
        <h1>{props.title}</h1>
      </header>
      <article className="policy-content">{props.children}</article>
    </main>
  );
}

/**
 * Shared legal effective date.
 *
 * @returns Effective-date line.
 */
function PolicyEffectiveDate(): React.JSX.Element {
  return <p className="effective-date">Effective August 1, 2026</p>;
}

/**
 * Not-found page.
 *
 * @returns Friendly 404 page.
 */
function NotFoundPage(): React.JSX.Element {
  return (
    <main className="not-found">
      <p className="eyebrow">404</p>
      <h1>Page not found</h1>
      <p>The page you requested does not exist.</p>
      <a className="primary link-button" href="/">Return home</a>
    </main>
  );
}

/**
 * Returns a safe error message for a public-page action.
 *
 * @param error Unknown caught value.
 * @returns Readable failure message.
 */
function readPublicError(error: unknown): string {
  return error instanceof Error ? error.message : "Something went wrong. Please try again.";
}

/**
 * Shared public footer with product, legal, and support links.
 *
 * @returns Public footer.
 */
function PublicFooter(): React.JSX.Element {
  return (
    <footer className="public-footer">
      <div>
        <a className="public-brand" href="/">
          <img src="/brand/papertiger-mark.png" alt="" width="38" height="38" />
          <span>PaperTiger</span>
        </a>
        <p>Prediction-market paper trading application and API.</p>
      </div>
      <div className="footer-links">
        <div>
          <strong>Product</strong>
          <a href="/pricing">Pricing</a>
          <a href="/about">About</a>
          <a href="/integrations">AI integrations</a>
          <a href="/docs">API docs</a>
          <a href="/support">Support</a>
        </div>
        <div>
          <strong>Trust</strong>
          <a href="/security">Security</a>
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
          <a href="/disclaimer">Disclaimer</a>
        </div>
      </div>
      <div className="footer-bottom">
        <span>© 2026 Disco Media</span>
        <span>Polymarket and Kalshi are independent third parties.</span>
      </div>
    </footer>
  );
}
